import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useState, useMemo, useRef, useEffect } from "react";
import { socket } from "../socket";
import "./css/Topbar.css";

import {
  FiUser,
  FiLock,
  FiSettings,
  FiHelpCircle,
  FiLogOut,
  FiChevronRight,
  FiBell,
  FiSearch,
  FiShield,
} from "react-icons/fi";

const pageMeta = {
  "/": { title: "Dashboard", subtitle: "" },
  "/bookings": { title: "Bookings", subtitle: "Manage all ride bookings" },
  "/drivers": { title: "Drivers", subtitle: "Manage registered drivers" },
  "/users": { title: "Passengers", subtitle: "Manage app passengers" },
  "/earnings": { title: "Earnings", subtitle: "Revenue & payout overview" },
  "/settings": { title: "Settings", subtitle: "App configuration" },
  "/passenger-wallet": {
    title: "Passenger Wallet",
    subtitle: "Manage passenger wallet balances",
  },
  "/suggestions": {
    title: "Suggestions",
    subtitle: "Driver suggestions & feedback",
  },
  "/profile": { title: "Profile", subtitle: "Your account details" },
  "/change-password": {
    title: "Change Password",
    subtitle: "Update your password",
  },
};

export default function Topbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const meta = pageMeta[location.pathname] || {
    title: "Moovuruli Private Limited",
    subtitle: "Safe and reliable ride-hailing services",
  };

  // ✅ Get user data
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem("admin");
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }, []);

  // ✅ Get initials
  const getInitials = (name) => {
    if (!name) return "AD";
    const words = name.trim().split(" ").filter(Boolean);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const userInitials = getInitials(userData?.name);
  const userName = userData?.name || "Admin";
  const userEmail = userData?.email || "admin@moovuruli.com";
  const userRole = userData?.role || "Super Admin";

  // ✅ Dynamic subtitle
  const getSubtitle = () => {
    if (location.pathname === "/") {
      return `Welcome back, ${userName}`;
    }
    return meta.subtitle;
  };

  // ✅ Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  // ✅ Navigate and close menu
  const handleNavigate = (path) => {
    setShowMenu(false);
    navigate(path);
  };

  // ✅ Logout
  const handleLogout = () => {
    socket.emit("admin-offline");
    socket.disconnect();
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    localStorage.removeItem("adminId");
    localStorage.removeItem("role");
    localStorage.removeItem("roleId");
    dispatch({ type: "LOGOUT" });
    navigate("/login");
  };

  // ✅ Menu items config
  const menuItems = [
    {
      label: "My Profile",
      icon: <FiUser size={15} />,
      onClick: () => handleNavigate("/profile"),
      show: true,
    },
    {
      label: "Change Password",
      icon: <FiLock size={15} />,
      onClick: () => handleNavigate("/change-password"),
      show: true,
    },
    {
      label: "Settings",
      icon: <FiSettings size={15} />,
      onClick: () => handleNavigate("/settings"),
      show: true,
    },
    {
      label: "Activity Log",
      icon: <FiShield size={15} />,
      onClick: () => handleNavigate("/activity-log"),
      show: true,
    },
    {
      label: "Help & Support",
      icon: <FiHelpCircle size={15} />,
      onClick: () => handleNavigate("/help"),
      show: true,
    },
  ];

  return (
    <header className="topbar">
      <div className="topbar-left">
        <h2>{meta.title}</h2>
        <p>{getSubtitle()}</p>
      </div>
      <div className="topbar-right">
        {/* Notifications */}
        <div className="icon-btn" title="Notifications">
          <FiBell size={16} />
          <div className="notif-dot"></div>
        </div>

        {/* Search */}
        <div className="icon-btn" title="Search">
          <FiSearch size={16} />
        </div>

        {/* ✅ Avatar + Dropdown */}
        <div className="tb-avatar-wrap" ref={menuRef}>
          <div
            className="user-avatar"
            title={userName}
            onClick={() => setShowMenu(!showMenu)}
            style={{ cursor: "pointer" }}
          >
            {userInitials}
          </div>

          {/* ✅ Dropdown Menu */}
          {showMenu && (
            <div className="tb-dropdown">
              {/* User Info Header */}
              <div className="tb-dropdown-header">
                <div
                  className="tb-dropdown-avatar"
                  style={{
                    background: `hsl(${(userName?.charCodeAt(0) || 0) * 15}, 50%, 85%)`,
                    color: `hsl(${(userName?.charCodeAt(0) || 0) * 15}, 60%, 35%)`,
                  }}
                >
                  {userInitials}
                </div>
                <div className="tb-dropdown-user-info">
                  <div className="tb-dropdown-name">{userName}</div>
                  <div className="tb-dropdown-email">{userEmail}</div>
                  <span className="tb-dropdown-role">{userRole}</span>
                </div>
              </div>

              {/* Divider */}
              <div className="tb-dropdown-divider" />

              {/* Menu Items */}
              <div className="tb-dropdown-menu">
                {menuItems
                  .filter((item) => item.show)
                  .map((item, index) => (
                    <button
                      key={index}
                      className="tb-dropdown-item"
                      onClick={item.onClick}
                    >
                      <span className="tb-dropdown-item-icon">{item.icon}</span>
                      <span className="tb-dropdown-item-label">
                        {item.label}
                      </span>
                      <FiChevronRight size={14} className="tb-dropdown-arrow" />
                    </button>
                  ))}
              </div>

              {/* Divider */}
              <div className="tb-dropdown-divider" />

              {/* Logout */}
              <div className="tb-dropdown-menu">
                <button
                  className="tb-dropdown-item tb-dropdown-logout"
                  onClick={handleLogout}
                >
                  <span className="tb-dropdown-item-icon">
                    <FiLogOut size={15} />
                  </span>
                  <span className="tb-dropdown-item-label">Logout</span>
                </button>
              </div>

              {/* Footer */}
              <div className="tb-dropdown-footer">Moovuruli Admin v1.0</div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
